import { createSelector } from '@reduxjs/toolkit';
import {
  SET_REFERRAL_SUMMARY,
  SET_REFERRAL_DATA,
  APPEND_REFERRAL_DATA,
  SET_BANNER_DATA,
  SET_FILTERS,
  SET_SORT,
  SET_PAGINATION,
  RESET_PAGINATION,
  SET_LAST_FETCHED_STATE,
  CLEAR_REFERRAL_DATA,
  SET_LOADING,
  SET_LOADING_MORE,
  SET_BANNER_LOADING,
  SET_ERROR,
  CLEAR_ERROR,
  SET_SELECTED_REFERRAL,
  SET_HAS_FETCHED_SUMMARY_METRICS,
  ReferralsState,
  ReferralStats,
  ReferralActions,
} from '../components/referrals/referrals.types';

// Initial state
const today = new Date();

const initialState: ReferralsState = {
  referralSummary: null,
  referralData: null,
  bannerData: null,
  selectedReferral: null,
  filters: {
    dateRange: 'Last 30 days',
    startDate: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000),
    endDate: today,
    status: 'ALL',
    searchQuery: '',
  },
  sort: {
    field: 'referred_time',
    order: 'desc',
  },
  pagination: {
    lastFetchedId: null,
    resultPerPage: 10,
    nextOrPrev: 'first',
    lastSortedColumnValue: null,
    isNextPresent: false,
    lastRowIdPrev: null,
    lastSortedColumnValueNext: null,
    lastSortedColumnValuePrev: null,
  },
  lastFetchedFilters: null,
  lastFetchedSort: null,
  loading: false,
  loadingMore: false,
  bannerLoading: false,
  error: null,
  hasFetchedSummaryMetrics: false,
};

function referralsReducer(
  state = initialState,
  action: ReferralActions,
): ReferralsState {
  switch (action.type) {
    case SET_REFERRAL_SUMMARY:
      return {
        ...state,
        referralSummary: action.payload,
      };
    case SET_REFERRAL_DATA:
      return {
        ...state,
        referralData: action.payload,
      };
    case APPEND_REFERRAL_DATA: {
      // For infinite scroll - append new data to existing data
      const existingData = state.referralData?.data?.referrals_data || [];
      const newData = action.payload?.data?.referrals_data || [];
      return {
        ...state,
        referralData: {
          ...action.payload,
          data: {
            ...action.payload.data,
            referrals_data: [...existingData, ...newData],
          },
        },
      };
    }
    case SET_BANNER_DATA:
      return {
        ...state,
        bannerData: action.payload,
      };
    case SET_BANNER_LOADING:
      return {
        ...state,
        bannerLoading: action.payload,
      };
    case SET_SELECTED_REFERRAL:
      return {
        ...state,
        selectedReferral: action.payload,
      };
    case SET_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };
    case SET_SORT:
      return {
        ...state,
        sort: action.payload,
      };
    case SET_PAGINATION:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          ...action.payload,
        },
      };
    case RESET_PAGINATION:
      return {
        ...state,
        pagination: {
          lastFetchedId: null,
          resultPerPage: 10,
          nextOrPrev: 'first',
          lastSortedColumnValue: null,
          isNextPresent: false,
          lastRowIdPrev: null,
          lastSortedColumnValueNext: null,
          lastSortedColumnValuePrev: null,
        },
      };
    case SET_LAST_FETCHED_STATE:
      return {
        ...state,
        lastFetchedFilters: action.payload.filters,
        lastFetchedSort: action.payload.sort,
      };
    case CLEAR_REFERRAL_DATA:
      return {
        ...state,
        referralData: null,
        lastFetchedFilters: null,
        lastFetchedSort: null,
        pagination: {
          lastFetchedId: null,
          resultPerPage: 10,
          nextOrPrev: 'first',
          lastSortedColumnValue: null,
          isNextPresent: false,
          lastRowIdPrev: null,
          lastSortedColumnValueNext: null,
          lastSortedColumnValuePrev: null,
        },
      };
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case SET_LOADING_MORE:
      return {
        ...state,
        loadingMore: action.payload,
      };
    case SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
        loadingMore: false,
      };
    case CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    case SET_HAS_FETCHED_SUMMARY_METRICS:
      return {
        ...state,
        hasFetchedSummaryMetrics: action.payload,
      };
    default:
      return state;
  }
}

export default referralsReducer;

// Basic selectors
export const selectBannerData = (state: { referrals: ReferralsState }) => state.referrals.bannerData;
export const selectSelectedReferral = (state: { referrals: ReferralsState }) => state.referrals.selectedReferral;
export const selectReferralFilters = (state: { referrals: ReferralsState }) => state.referrals.filters;
export const selectReferralSort = (state: { referrals: ReferralsState }) => state.referrals.sort;
export const selectReferralPagination = (state: { referrals: ReferralsState }) => state.referrals.pagination;
export const selectReferralsLoading = (state: { referrals: ReferralsState }) => state.referrals.loading;
export const selectReferralsLoadingMore = (state: { referrals: ReferralsState }) => state.referrals.loadingMore;
export const selectBannerLoading = (state: { referrals: ReferralsState }) => state.referrals.bannerLoading;
export const selectReferralsError = (state: { referrals: ReferralsState }) => state.referrals.error;

// Base selector for referral summary
const selectReferralSummary = (state: { referrals: ReferralsState }) => state.referrals.referralSummary;

// Base selector for referral data
const selectReferralData = (state: { referrals: ReferralsState }) => state.referrals.referralData;

// Memoized selector for referral stats
export const selectReferralStats = createSelector(
  [selectReferralSummary],
  (summary): ReferralStats => {
    if (summary?.data) {
      const referralSummary = summary.data.referral_summary;
      return {
        total_earnings: summary?.data?.total_earnings || 0,
        maximum_possible_earning: summary?.data?.maximum_possible_earning || 0,
        is_first_time_referrer: summary?.data?.is_first_time_referrer ?? false,
        program_start_date: summary?.data?.program_start_date,
        program_end_date: summary?.data?.program_end_date,
        referred: summary?.data?.stats?.referred || 0,
        onboarded: summary?.data?.stats?.onboarded || 0,
        expired: summary?.data?.stats?.expired || 0,
        referral_summary: referralSummary ? {
          expiring_soon: referralSummary.expiring_soon ?? null,
          closer_to_payout: referralSummary.closer_to_payout ?? null,
          needs_nudge: referralSummary.needs_nudge ?? null,
        } : null,
      };
    }

    return {
      total_earnings: 0,
      maximum_possible_earning: 0,
      is_first_time_referrer: false,
      referred: 0,
      onboarded: 0,
      expired: 0,
      referral_summary: null,
    };
  },
);

// Memoized selector for filtered referrals
export const selectFilteredReferrals = createSelector(
  [selectReferralData],
  (referralData): any[] => {
    return referralData?.data?.referrals_data || [];
  },
);